import React, { Fragment } from 'react'
import { useCallback } from 'sb-hooks'
import cx from 'classnames'

import { Href } from 'components/_new/controls'

import s from './ButtonBase.scss'


export const sizes = [ 'xs', 'sm', 'md' ] as const
export const colors = [ 'brand', 'brandBorder', 'white', 'transparent', 'whiteActiveBrand', 'brandTransparent', 'black', 'whiteColorBrand', null ] as const

type Size = typeof sizes[number]
type Color = typeof colors[number]

export type ButtonBaseProps = {
  // basic
  children?: React.ReactNode
  afterNode?: React.ReactNode
  className?: string
  onClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>

  // links
  to?: string
  toOLD?: string
  redirect?: string

  // modifiers
  size: Size
  color: Color

  fullWidth?: boolean
  fullWidthOnMobile?: boolean
  disabled?: boolean
  loading?: boolean

  tag?: string
  type?: string
  id?: string
  tabIndex?: number
  dataTestId?: string
  dataAttributes?: any

  // ADA
  ariaLive?: string
  ariaLabel?: string
  ariaLabelledBy?: string
  ariaDescribedBy?: string
}

const ButtonBase: React.FunctionComponent<ButtonBaseProps> = (props) => {
  const {
    // basic
    children, id, className, afterNode, onClick,

    // modifiers
    fullWidth, fullWidthOnMobile, disabled, loading,

    // links
    to, toOLD, redirect,

    // misc
    size, color, tag, type, tabIndex, dataAttributes, dataTestId,

    // ADA
    ariaLive,
    ariaLabel,
    ariaLabelledBy,
    ariaDescribedBy,
  } = props

  // Node -------------------------- /

  let node: string | React.ElementType = tag

  if (to || toOLD || redirect) {
    node = Href
  }

  // Classes ----------------------- /

  const nodeClassName = cx(s.button, className, 'button', {
    [s[size]]: true,
    [s[color]]: color,
    [s.fullWidth]: fullWidth,
    [s.fullWidthOnMobile]: fullWidthOnMobile,
    [s.disabled]: disabled,
    [s.notDisabled]: !disabled,
    [s.loading]: loading,
  })

  // Props -------------------------- /

  const handleClick = useCallback((event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (!disabled && !loading && typeof onClick === 'function') {
      onClick(event)
    }
  }, [ onClick, disabled, loading ])

  let nodeProps: any = {
    className: nodeClassName,
    id,
    type,
    disabled,
    tabIndex,
    onClick: handleClick,
    'data-testid': dataTestId,
    'aria-live': ariaLive,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
  }

  if (to || toOLD || redirect) {
    nodeProps = {
      ...nodeProps,
      to,
      href: toOLD,
      toTab: redirect,
    }
  }
  else {
    nodeProps.type = type
  }

  if (dataAttributes) {
    nodeProps = {
      ...nodeProps,
      ...dataAttributes,
    }
  }

  const nodeChildren = (
    <Fragment>
      {children}
      {afterNode}
    </Fragment>
  )

  return React.createElement(
    node,
    nodeProps,
    nodeChildren
  )
}


ButtonBase.defaultProps = {
  tag: 'button',
  size: 'md',
  color: 'transparent',
}


export default ButtonBase
